import React from "react";
import {
  ImageUpload,
  ImageUploadMetadata,
  Input,
  ToggleSwitch,
} from "../../../../components";
import { useTranslations } from "../../../../contexts";
import type { SidebarFormProps } from "./SidebarForm";
import { stripQuery, withSiteId } from "../../../../helpers/imageUri";
import { normalizeHttpUrl } from "../../../../helpers/externalUrl";
import { DesignerSectionHeading } from "./DesignerSectionHeading";

export const SideFormHero: React.FC<SidebarFormProps> = (props) => {
  const i18n = useTranslations();
  const { siteId } = props;
  const hasExternalLinkButton =
    Boolean(props.form.hero?.secondary_cta) ||
    Boolean(props.form.hero?.secondary_cta_link);
  const [showExternalLinkButton, setShowExternalLinkButton] = React.useState(
    hasExternalLinkButton
  );

  React.useEffect(() => {
    setShowExternalLinkButton(hasExternalLinkButton);
  }, [hasExternalLinkButton]);

  const setImageMetadata = (uri: string, metadata?: ImageUploadMetadata) => {
    if (!metadata) return;
    const cleanedUri = stripQuery(uri);
    props.setImageMetadata?.({
      ...props.imageMetadata,
      [cleanedUri]: metadata,
    });
  };

  return (
    <div
      id="hero-form"
      className="border-b p-6"
      data-preview-section="hero"
    >
      <div className="pb-4">
        <DesignerSectionHeading
          className="pb-2"
          title={i18n.logo}
          helpLabel={`${i18n.designerHelpFor} ${i18n.logo}`}
          helpText={i18n.designerLogoHelp}
        />
        <ImageUpload
          className="max-w-[100px]"
          usage="hero"
          image={withSiteId(props.form.hero?.image_uri, siteId)}
          onChange={({ uri, metadata }) => {
            props.setForm({
              ...props.form,
              hero: {
                ...props.form.hero,
                image_uri: stripQuery(uri),
              },
            });
            setImageMetadata(uri, metadata);
          }}
          onDelete={() =>
            props.setForm({
              ...props.form,
              hero: { ...props.form.hero, image_uri: null },
            })
          }
        />
      </div>
      <DesignerSectionHeading
        className="pb-4"
        title={i18n.introduction}
        helpLabel={`${i18n.designerHelpFor} ${i18n.introduction}`}
        helpText={i18n.designerIntroductionHelp}
      />
      <Input
        className="pb-2"
        placeholder={i18n["title"]}
        example={i18n.designerHeroTitlePlaceholder}
        help={{
          label: `${i18n.designerHelpFor} ${i18n.title}`,
          content: i18n.designerHeroTitleHelp,
        }}
        value={props.form.hero?.title || ""}
        onChange={(event) =>
          props.setForm({
            ...props.form,
            hero: { ...props.form.hero, title: event.target.value },
          })
        }
      />
      <Input
        asTextarea
        className="pb-2"
        placeholder={i18n["description"]}
        example={i18n.designerHeroDescriptionPlaceholder}
        help={{
          label: `${i18n.designerHelpFor} ${i18n.description}`,
          content: i18n.designerHeroDescriptionHelp,
        }}
        value={props.form.hero?.description || ""}
        onChange={(event) =>
          props.setForm({
            ...props.form,
            hero: { ...props.form.hero, description: event.target.value },
          })
        }
      />

      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="flex items-start gap-4">
          <div className="grow">
            <DesignerSectionHeading
              level={3}
              title={i18n["External link button"]}
              helpLabel={`${i18n.designerHelpFor} ${i18n["External link button"]}`}
              helpText={i18n.designerExternalLinkHelp}
            />
            <p
              id="external-link-button-description"
              className="mt-1 text-sm text-gray-500"
            >
              {
                i18n[
                  "Add an optional button that opens another website in a new tab."
                ]
              }
            </p>
          </div>
          <ToggleSwitch
            aria-label={i18n["Show external link button"]}
            aria-describedby="external-link-button-description"
            aria-controls="external-link-button-fields"
            checked={showExternalLinkButton}
            onChange={(event) => {
              const isEnabled = event.target.checked;
              setShowExternalLinkButton(isEnabled);
              if (isEnabled) return;
              props.setForm({
                ...props.form,
                hero: {
                  ...props.form.hero,
                  secondary_cta: null,
                  secondary_cta_link: null,
                },
              });
            }}
          />
        </div>
        {showExternalLinkButton && (
          <div id="external-link-button-fields" className="mt-4 space-y-2">
            <Input
              placeholder={i18n["Button text"]}
              maxLength={100}
              example={i18n.designerButtonLabelPlaceholder}
              help={{
                label: `${i18n.designerHelpFor} ${i18n["Button text"]}`,
                content: i18n.designerButtonTextHelp,
              }}
              value={props.form.hero?.secondary_cta || ""}
              onChange={(event) =>
                props.setForm({
                  ...props.form,
                  hero: {
                    ...props.form.hero,
                    secondary_cta: event.target.value,
                  },
                })
              }
            />
            <Input
              type="url"
              placeholder={i18n["External URL"]}
              maxLength={2048}
              pattern="[Hh][Tt][Tt][Pp][Ss]?://.+"
              example={i18n.designerButtonUrlPlaceholder}
              help={{
                label: `${i18n.designerHelpFor} ${i18n["External URL"]}`,
                content: i18n.designerButtonUrlHelp,
              }}
              value={props.form.hero?.secondary_cta_link || ""}
              onChange={(event) =>
                props.setForm({
                  ...props.form,
                  hero: {
                    ...props.form.hero,
                    secondary_cta_link: event.target.value,
                  },
                })
              }
              onBlur={(event) => {
                const normalizedUrl = normalizeHttpUrl(event.target.value);
                if (normalizedUrl === event.target.value) return;
                props.setForm({
                  ...props.form,
                  hero: {
                    ...props.form.hero,
                    secondary_cta_link: normalizedUrl,
                  },
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
